import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const ToastContext = createContext();

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Styles
const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
`;

const ToastItem = styled.div`
  min-width: 320px;
  max-width: 450px;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  pointer-events: auto;
  cursor: pointer;
  animation: ${props => props.$isExiting ? slideOut : slideIn} 0.3s ease forwards;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  ${props => {
    switch (props.$type) {
      case 'success':
        return `
          background: linear-gradient(135deg, #27ae60, #2ecc71);
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
      case 'error':
        return `
          background: linear-gradient(135deg, #c0392b, #e74c3c);
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
      case 'warning':
        return `
          background: linear-gradient(135deg, #d35400, #e67e22);
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
      case 'info':
      default:
        return `
          background: linear-gradient(135deg, #632ce4, #9d50bb);
          border: 1px solid rgba(255, 255, 255, 0.2);
        `;
    }
  }}
`;

const ToastIcon = styled.span`
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
`;

const ToastTitle = styled.h4`
  color: #fff;
  margin: 0 0 4px 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ToastMessage = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-size: 1rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const getIcon = (type) => {
  switch (type) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'warning':
      return '⚠';
    case 'info':
    default:
      return 'ℹ';
  }
};

const getDefaultTitle = (type) => {
  switch (type) {
    case 'success':
      return 'Succès';
    case 'error':
      return 'Erreur';
    case 'warning':
      return 'Attention';
    case 'info':
    default:
      return 'Information';
  }
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(toast =>
      toast.id === id ? { ...toast, isExiting: true } : toast
    ));

    // Supprimer après l'animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  const showToast = useCallback((message, options = {}) => {
    const {
      type = 'info',
      title = null,
      duration = 5000
    } = options;

    const id = Date.now() + Math.random();

    const toast = {
      id,
      message,
      type,
      title: title || getDefaultTitle(type),
      isExiting: false
    };

    setToasts(prev => [...prev, toast]);

    // Auto-suppression
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }

    return id;
  }, [removeToast]);

  // Raccourcis pour les différents types
  const success = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: 'success' });
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: 'error' });
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: 'warning' });
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: 'info' });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <ToastContainer>
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            $type={toast.type}
            $isExiting={toast.isExiting}
            onClick={() => removeToast(toast.id)}
          >
            <ToastIcon>{getIcon(toast.type)}</ToastIcon>
            <ToastContent>
              <ToastTitle>{toast.title}</ToastTitle>
              <ToastMessage>{toast.message}</ToastMessage>
            </ToastContent>
            <CloseButton onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}>
              ✕
            </CloseButton>
          </ToastItem>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
